import _ from 'lodash';
import $ from 'jquery';

import React, { useState, useEffect } from 'react';
import { Picture } from 'models';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { HvPleaseWait, HvButton, HvModal } from 'components';
import { MODAL, ALERT } from 'config/constants';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useListing, useAuth, useAlert } from 'hooks';
import { brokerSwapPicturesAsync, hideModal, fetchListingAsync } from 'state/actions';
import { images } from 'assets/images';
import { getPictureServiceOfferIdsCopy } from 'utils';
import { ThumbnailList } from '../components';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    padding: 5,
    alignItems: 'center',
    overflowX: 'hidden',
  },
  description: {
    fontSize: 14,
    marginBottom: 10,
    maxWidth: 900,
  },
  button: {
    width: 200,
    marginLeft: 10,
  },
  thumbnailList: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    flexGrow: 1,
    overflowY: 'auto',
    maxHeight: 400,
    maxWidth: 685,
    overflowX: 'hidden',
  },
  cancelButton: {
    backgroundColor: 'white',
    marginRight: 20,
    color: '#00bdf2',
    border: '1px solid #00bdf2',
  },
  summary: {
    fontSize: 12,
  },
  sectionTitle: {
    color: 'white',
    fontSize: 17,
    backgroundColor: '#00bdf2',
    paddingLeft: 5,
  },
  sectionBox: {
    border: 'solid 4px #00bdf2',
    borderRadius: 3,
    display: 'flex',
    flexDirection: 'column',
    width: '50wh',
    height: 440,
    maxWidth: '46.875em',
    marginTop: 10,
    '& div:first-child': {
      marginRight: 0,
    },
    '@media (max-width: 767px)': {
      marginBottom: 20,
      height: 300,
      minWidth: 250,
    },
  },
  subSectionBox: {
    display: 'flex',
    maxWidth: '100%',
    height: '100%',
    overflowX: 'hidden',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    '@media (max-width: 767px)': {
      flexDirection: 'column',
    },
  },
  swapArrow: {
    maxWidth: 50,
    margin: 10,
    maxHeight: 50,
  },
});

function PictureSwapModal() {
  const styles = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { displayAlert } = useAlert();
  const { token, user } = useAuth();
  const [size, setSize] = useState(null);
  const [watermarkPictures, setWatermarkPictures] = useState([]);
  const [picturesToExchange, setPicturesToExchange] = useState([]);
  const [picturesToExchangeWith, setPicturesToExchangeWith] = useState([]);
  const [pictureServiceOfferIds, setPictureServiceOfferIds] = useState({
    traditional: { id: 0, price: 0 },
    drone: { id: 0, price: 0 },
    blueHour: { id: 0, price: 0 },
  });
  const [activeSections, setActiveSections] = useState({
    traditional: false,
    drone: false,
    blueHour: false,
    traditionalWatermark: false,
    droneWatermark: false,
    blueHourWatermark: false,
  });
  const [load, setLoad] = useState(true);
  const [thumbnailsContentWidth, setThumbnailsContentWidth] = useState(0);

  const path = window.location.pathname;
  const arr = path.split('/');
  const distributionToken = arr[arr.length - 1];
  const { listing, addedBrokerSwapPicturesStatus } = useListing(distributionToken);
  const [pictures, setPictures] = useState([]);

  const resizeThumbnails = _.debounce(() => {
    const bodyWidth = $('#root').eq(0).width();
    const width = bodyWidth > 500 ? 222 : bodyWidth / 2 - 5;
    const height = width / 1.5;
    setSize({ width, height });
    const count = Math.floor(bodyWidth / width);
    const thumbnailsWidth = count * width;
    setThumbnailsContentWidth(thumbnailsWidth);
    const thumbnails = $('#thumbnails');
    if (!!thumbnails.length && thumbnailsWidth !== thumbnails.eq(0).width()) {
      thumbnails.eq(0).width(thumbnailsWidth);
    }
  }, 100);

  const onClose = () => {
    setPicturesToExchange([]);
    setPicturesToExchangeWith([]);
    setPictures([]);
    setWatermarkPictures([]);
    dispatch(hideModal(MODAL.PICTURE_SWAP));
  };

  const refresh = () => {
    dispatch(fetchListingAsync(distributionToken));
  };

  useEffect(() => {
    const pictureServiceOfferIdsCopy = pictureServiceOfferIds;
    const data = getPictureServiceOfferIdsCopy(
      pictureServiceOfferIdsCopy,
      listing.broker.subscription_id,
    );
    setPictureServiceOfferIds(data);
  });

  useEffect(() => {
    if (addedBrokerSwapPicturesStatus) {
      if (addedBrokerSwapPicturesStatus === 200) {
        displayAlert(ALERT.SUCCESS, intl.formatMessage({ id: 'deliver.pictureSwapModal.success' }));
      } else {
        displayAlert(ALERT.ERROR, intl.formatMessage({ id: 'deliver.pictureSwapModal.error' }));
      }
      onClose();
      refresh();
    }
  }, [addedBrokerSwapPicturesStatus]);

  useEffect(() => {
    if (listing) {
      setPictures(
        listing.pictures.filter((picture: Picture) => {
          return picture.show_watermark === false && picture.was_downloaded === false;
        }),
      );
      setWatermarkPictures(
        listing.pictures.filter((picture: Picture) => {
          return picture.show_watermark === true && picture.was_downloaded === false;
        }),
      );
      setLoad(false);
    }
    resizeThumbnails();
    window.addEventListener('resize', resizeThumbnails);
    return () => {
      window.removeEventListener('resize', resizeThumbnails);
    };
  }, [listing]);

  useEffect(() => {
    const activeSectionsCopy = activeSections;

    for (let i = 0; i < pictures.length; i++) {
      if (pictures[i].service_offer.id === pictureServiceOfferIds.traditional.id) {
        activeSectionsCopy.traditional = true;
      } else if (pictures[i].service_offer.id === pictureServiceOfferIds.drone.id) {
        activeSectionsCopy.drone = true;
      } else if (pictures[i].service_offer.id === pictureServiceOfferIds.blueHour.id) {
        activeSectionsCopy.blueHour = true;
      } else if (
        activeSectionsCopy.traditional === true &&
        activeSectionsCopy.drone === true &&
        activeSectionsCopy.blueHour === true
      ) {
        break;
      }
    }

    for (let i = 0; i < watermarkPictures.length; i++) {
      if (watermarkPictures[i].service_offer.id === pictureServiceOfferIds.traditional.id) {
        activeSectionsCopy.traditionalWatermark = true;
      } else if (watermarkPictures[i].service_offer.id === pictureServiceOfferIds.drone.id) {
        activeSectionsCopy.droneWatermark = true;
      } else if (watermarkPictures[i].service_offer.id === pictureServiceOfferIds.blueHour.id) {
        activeSectionsCopy.blueHourWatermark = true;
      }
    }

    setActiveSections(activeSectionsCopy);
  }, [pictureServiceOfferIds, pictures, watermarkPictures, listing]);

  function addSelectedPicture(picture: Picture) {
    let picturesToExchangeCopy = [];

    if (picture.show_watermark === true) {
      picturesToExchangeCopy = [...picturesToExchangeWith];
    } else {
      picturesToExchangeCopy = [...picturesToExchange];
    }

    if (picturesToExchangeCopy.includes(picture)) {
      picturesToExchangeCopy.splice(picturesToExchangeCopy.indexOf(picture), 1);
    } else {
      picturesToExchangeCopy.push(picture);
    }

    if (picture.show_watermark === true) {
      setPicturesToExchangeWith(picturesToExchangeCopy);
    } else {
      setPicturesToExchange(picturesToExchangeCopy);
    }
  }

  async function onSubmit() {
    const picturesToExchangeIds = picturesToExchange.map((picture: Picture) => {
      return picture.id;
    });
    const picturesToExchangeWithIds = picturesToExchangeWith.map((picture: Picture) => {
      return picture.id;
    });
    dispatch(brokerSwapPicturesAsync(token, picturesToExchangeWithIds, picturesToExchangeIds));
  }

  function checkForSamePictureAmount() {
    const traditionalCount = picturesToExchange.filter((pic) => {
      return pic.service_offer.id === pictureServiceOfferIds.traditional.id;
    }).length;

    const droneCount = picturesToExchange.filter((pic) => {
      return pic.service_offer.id === pictureServiceOfferIds.drone.id;
    }).length;

    const blueHourCount = picturesToExchange.filter((pic) => {
      return pic.service_offer.id === pictureServiceOfferIds.blueHour.id;
    }).length;

    const traditionalCountWaterMark = picturesToExchangeWith.filter((pic) => {
      return pic.service_offer.id === pictureServiceOfferIds.traditional.id;
    }).length;

    const droneCountWaterMark = picturesToExchangeWith.filter((pic) => {
      return pic.service_offer.id === pictureServiceOfferIds.drone.id;
    }).length;

    const blueHourCountWaterMark = picturesToExchangeWith.filter((pic) => {
      return pic.service_offer.id === pictureServiceOfferIds.blueHour.id;
    }).length;

    if (
      traditionalCount === traditionalCountWaterMark &&
      droneCount === droneCountWaterMark &&
      blueHourCount === blueHourCountWaterMark
    ) {
      return false;
    }
    return true;
  }

  const buttons = (
    <Box>
      <HvButton className={styles.cancelButton} onClick={onClose}>
        {intl.formatMessage({ id: 'deliver.photos.cancel' })}
      </HvButton>
      <HvButton
        onClick={() => {
          onSubmit();
        }}
        disabled={
          checkForSamePictureAmount() ||
          picturesToExchangeWith.length <= 0 ||
          picturesToExchange.length <= 0
        }
      >
        {intl.formatMessage({ id: 'deliver.pictureSwapModal.exchangeButton' })}
      </HvButton>
    </Box>
  );

  function loadSection(sectionTitle: string, serviceOfferId: number, toBeExchange: boolean) {
    return (
      <Box className={styles.sectionBox}>
        <Typography className={styles.sectionTitle}>
          {`${intl.formatMessage({ id: `deliver.additionalPictures.${sectionTitle}` })} (${
            (toBeExchange ? picturesToExchange : picturesToExchangeWith).filter((picture) => {
              return picture.service_offer.id === serviceOfferId;
            }).length
          })`}
        </Typography>
        <Box className={styles.thumbnailList}>
          {(toBeExchange ? [...pictures] : [...watermarkPictures])
            .filter((sectionPictures) => {
              return sectionPictures.service_offer.id === serviceOfferId;
            })
            .map((pic: Picture) => {
              return (
                <ThumbnailList
                  key={pic.id}
                  picture={pic}
                  selectedPictures={toBeExchange ? picturesToExchange : picturesToExchangeWith}
                  addSelectedPicture={() => {
                    addSelectedPicture(pic);
                  }}
                  type='additionalPicturesList'
                  size={size}
                  bodyWidth={thumbnailsContentWidth}
                />
              );
            })}
        </Box>
      </Box>
    );
  }

  const modalContent = (
    <Box className={styles.content}>
      <Typography className={styles.description}>
        {intl.formatMessage({ id: 'deliver.pictureSwapModal.description' })}
      </Typography>
      {activeSections.traditionalWatermark && activeSections.traditional && (
        <Box className={styles.subSectionBox}>
          {loadSection('traditionalImgTitle', pictureServiceOfferIds.traditional.id, true)}
          <img className={styles.swapArrow} src={images.swapArrow} alt='swapping arrow' />
          {loadSection('traditionalImgTitle', pictureServiceOfferIds.traditional.id, false)}
        </Box>
      )}
      {activeSections.droneWatermark && activeSections.drone && (
        <Box className={styles.subSectionBox}>
          {loadSection('droneImgTitle', pictureServiceOfferIds.drone.id, true)}
          <img className={styles.swapArrow} src={images.swapArrow} alt='swapping arrow' />
          {loadSection('droneImgTitle', pictureServiceOfferIds.drone.id, false)}
        </Box>
      )}
      {activeSections.blueHourWatermark && activeSections.blueHour && (
        <Box className={styles.subSectionBox}>
          {loadSection('blueHourImgTitle', pictureServiceOfferIds.blueHour.id, true)}
          <img className={styles.swapArrow} src={images.swapArrow} alt='swapping arrow' />
          {loadSection('blueHourImgTitle', pictureServiceOfferIds.blueHour.id, false)}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {!load ? (
        <HvModal
          maxWidth='lg'
          buttons={buttons}
          title={intl.formatMessage({ id: 'deliver.pictureSwapModal.tile' })}
          onClose={onClose}
        >
          {modalContent}
        </HvModal>
      ) : (
        <HvPleaseWait />
      )}
    </>
  );
}

export default PictureSwapModal;
